/**
 * Effekter catalog sections on /produkter?category=Effekter.
 * Classification is use-case via slug/id — product display names must not move tiers.
 */

export type EffekterTierId = 'rok_atmosfar' | 'konfetti_show' | 'gnistor_sceneffekter';

export const EFFEKTER_TIER_STICKY_NAV = false;

export const EFFEKTER_TIER_ORDER: readonly EffekterTierId[] = [
  'rok_atmosfar',
  'konfetti_show',
  'gnistor_sceneffekter',
] as const;

export const EFFEKTER_TIER_COPY: Readonly<
  Record<EffekterTierId, { title: string; blurb: string }>
> = {
  rok_atmosfar: {
    title: 'Rök & atmosfär',
    blurb:
      'Skapa djup, stämning och synligare ljuseffekter med rök, haze och atmosfäreffekter.',
  },
  konfetti_show: {
    title: 'Konfetti & showeffekter',
    blurb: 'Effekter som skapar stora ögonblick på dansgolv, scen och event.',
  },
  gnistor_sceneffekter: {
    title: 'Gnistor & sceneffekter',
    blurb:
      'Effekter med hög visuell impact för entréer, dansgolv, scen och större produktioner.',
  },
};

/** Explicit live mapping (verified 2026-09-18). One section per product. */
export const EFFEKTER_TIER_BY_SLUG: Readonly<Record<string, EffekterTierId>> = {
  // Rök & atmosfär
  'mellanstor-rokmaskin-med-fjarrkontroll-1000w': 'rok_atmosfar',
  'stor-rokmaskin-kraftfull-rokmaskin-med-tradbunden-fjarrkontroll': 'rok_atmosfar',
  'mellan-haze': 'rok_atmosfar',
  'stor-haze': 'rok_atmosfar',
  'lagrokmaskin': 'rok_atmosfar',
  'professionell-snomaskin-ink-2-5l-vatska': 'rok_atmosfar',
  'rok-och-bubbelmaskin-med-led-effekt': 'rok_atmosfar',
  // Konfetti & showeffekter
  'konfettimaskin-kraftfull-1500w-med-rgb-ljus-justerbar-kastvinkel': 'konfetti_show',
  'premium-konfettikanon': 'konfetti_show',
  'konfettikanon-skjuter-konfettiror': 'konfetti_show',
  'konfetti-gevar': 'konfetti_show',
  'red-button-avfyrning-till-elektrisk-konfettimaskin': 'konfetti_show',
  'ersatt-brollopsriset-med-bubblor-rok-och-ljusshow': 'konfetti_show',
  // Gnistor & sceneffekter
  'kallgnistpaket-2-st-kallgnistmaskiner-sparkular-effekt': 'gnistor_sceneffekter',
  'kallgnistpaket-4-st-kallgnistmaskiner-sparkular-effekt': 'gnistor_sceneffekter',
  'co2-jet-kraftfull-scen-effekt-for-event-klubb-brollop': 'gnistor_sceneffekter',
  'vertical-fogger': 'gnistor_sceneffekter',
};

/** Stable product ids for the same live set (secondary SoT if slug missing). */
export const EFFEKTER_TIER_BY_ID: Readonly<Record<string, EffekterTierId>> = {
  '329da633-ecd3-4d2b-9aed-f475996e04f8': 'rok_atmosfar',
  '46a30844-36c7-4450-88bc-8ab193c5ff2c': 'rok_atmosfar',
  '69a5d76a-c401-4b51-9330-9f877959bb32': 'rok_atmosfar',
  '5edfd2e1-673c-46ad-80ac-f6ca44e7ec19': 'rok_atmosfar',
  '0207c585-a088-4c6a-9df4-b8628bdf252d': 'rok_atmosfar',
  'eed53e36-e23e-411f-aa56-e8142b8e0f4c': 'rok_atmosfar',
  '40128bcc-f5a6-4b2c-9f7a-cc475429e4c7': 'rok_atmosfar',
  '295c7dec-a5ef-4025-ba08-81316ab5695a': 'konfetti_show',
  '28985382-a90b-4a48-a254-99e0f8dc5436': 'konfetti_show',
  'ae041697-2421-4e9d-bac1-fea8f2b55c3b': 'konfetti_show',
  '70ae9110-329d-4b0e-a3e3-ba11f1053792': 'konfetti_show',
  'e6136516-bfb1-4b86-819d-85d49a6f2da4': 'konfetti_show',
  '00fab135-4a67-4e67-bf61-19186bd52382': 'konfetti_show',
  'f9f75c30-ea87-4794-906e-314ac9104960': 'gnistor_sceneffekter',
  'f71f0ad7-c41d-478f-a61d-b1778bdff0f2': 'gnistor_sceneffekter',
  '06a943ea-f3de-47e6-9ed0-3248fbf0d8bd': 'gnistor_sceneffekter',
  '769516f8-ffdb-4cbf-a004-cd0fa9650c4f': 'gnistor_sceneffekter',
};

export type EffekterTierProductRef = {
  id?: string;
  slug?: string | null;
  name?: string;
};

/**
 * Resolve section. Explicit slug, then id. Name is ignored.
 * Returns null for unmapped products (no implicit fallback section).
 */
export function resolveEffekterTier(
  product: EffekterTierProductRef
): EffekterTierId | null {
  const slug = String(product.slug ?? '')
    .trim()
    .toLowerCase();
  if (slug && EFFEKTER_TIER_BY_SLUG[slug]) {
    return EFFEKTER_TIER_BY_SLUG[slug];
  }
  const id = String(product.id ?? '').trim();
  if (id && EFFEKTER_TIER_BY_ID[id]) {
    return EFFEKTER_TIER_BY_ID[id];
  }
  return null;
}

export type EffekterTierGroup<T extends EffekterTierProductRef> = {
  tier: EffekterTierId;
  title: string;
  blurb: string;
  products: T[];
};

export function groupEffekterProducts<T extends EffekterTierProductRef>(
  products: readonly T[]
): EffekterTierGroup<T>[] {
  const buckets: Record<EffekterTierId, T[]> = {
    rok_atmosfar: [],
    konfetti_show: [],
    gnistor_sceneffekter: [],
  };
  for (const product of products) {
    const tier = resolveEffekterTier(product);
    if (!tier) continue;
    buckets[tier].push(product);
  }
  return EFFEKTER_TIER_ORDER.map((tier) => ({
    tier,
    title: EFFEKTER_TIER_COPY[tier].title,
    blurb: EFFEKTER_TIER_COPY[tier].blurb,
    products: buckets[tier],
  })).filter((g) => g.products.length > 0);
}
